import React, { useState, useEffect, useRef } from 'react';
import { FaLaptopCode, FaMobileAlt, FaServer, FaShieldAlt, FaRocket, FaChartLine,
    FaBrain, FaCloud, FaDatabase, FaCogs, FaNetworkWired, FaRobot } from 'react-icons/fa';
import { DiReact, DiNodejs, DiAws, DiDocker, DiPython, DiMongodb } from 'react-icons/di';
import { SiKubernetes, SiTensorflow, SiFirebase, SiBlockchaindotcom } from 'react-icons/si';
import '../main.css';
import logo from '../assets/logonav.png';

const TechMainSection = () => {
    const [activeTech, setActiveTech] = useState(0);
    const [hoveredItem, setHoveredItem] = useState(null);
    const sectionRef = useRef(null);

    const techSolutions = [
        {
            category: "DÉVELOPPEMENT FULL-STACK",
            items: [
                {
                    name: "Applications Web React/Next.js",
                    icon: <DiReact />,
                    color: "#61DAFB",
                    tech: ["React", "Next.js", "TypeScript", "Redux"],
                    description: "Applications modernes avec performances optimales"
                },
                {
                    name: "Apps Mobiles Cross-Platform",
                    icon: <FaMobileAlt />,
                    color: "#FF6B6B",
                    tech: ["React Native", "Flutter", "Swift", "Kotlin"],
                    description: "Solutions mobiles natives et hybrides"
                },
                {
                    name: "Microservices & APIs",
                    icon: <FaServer />,
                    color: "#4ECDC4",
                    tech: ["Node.js", "GraphQL", "REST", "gRPC"],
                    description: "Architecture microservices scalable"
                }
            ]
        },
        {
            category: "CLOUD & DEVOPS",
            items: [
                {
                    name: "Infrastructure Cloud AWS/Azure",
                    icon: <DiAws />,
                    color: "#FF9900",
                    tech: ["AWS", "Azure", "GCP", "Terraform"],
                    description: "Cloud scalable et sécurisé"
                },
                {
                    name: "Conteneurisation & Orchestration",
                    icon: <DiDocker />,
                    color: "#2496ED",
                    tech: ["Docker", "Kubernetes", "Helm", "Istio"],
                    description: "Conteneurs et orchestration avancés"
                },
                {
                    name: "CI/CD Pipelines",
                    icon: <FaRocket />,
                    color: "#FFD166",
                    tech: ["Jenkins", "GitLab CI", "GitHub Actions", "ArgoCD"],
                    description: "Automatisation déploiement"
                }
            ]
        },
        {
            category: "INTELLIGENCE & DATA",
            items: [
                {
                    name: "IA & Machine Learning",
                    icon: <FaBrain />,
                    color: "#9C27B0",
                    tech: ["TensorFlow", "PyTorch", "Scikit-learn", "OpenCV"],
                    description: "Solutions d'intelligence artificielle"
                },
                {
                    name: "Big Data & Analytics",
                    icon: <FaDatabase />,
                    color: "#2196F3",
                    tech: ["Spark", "Hadoop", "Kafka", "Elasticsearch"],
                    description: "Analyse données en temps réel"
                },
                {
                    name: "Blockchain & Web3",
                    icon: <SiBlockchaindotcom />,  // Fixed here
                    color: "#3DDC84",
                    tech: ["Solidity", "Web3.js", "Smart Contracts", "NFTs"],
                    description: "Solutions blockchain innovantes"
                }
            ]
        },
        {
            category: "SÉCURITÉ & MONITORING",
            items: [
                {
                    name: "Cybersécurité Avancée",
                    icon: <FaShieldAlt />,
                    color: "#FF5252",
                    tech: ["Pentesting", "Firewalls", "Encryption", "Zero Trust"],
                    description: "Protection multi-niveaux"
                },
                {
                    name: "Monitoring & Observability",
                    icon: <FaChartLine />,
                    color: "#00BCD4",
                    tech: ["Prometheus", "Grafana", "ELK Stack", "Datadog"],
                    description: "Surveillance temps réel"
                },
                {
                    name: "Automation & RPA",
                    icon: <FaRobot />,
                    color: "#FF9800",
                    tech: ["Python", "PowerShell", "Ansible", "UiPath"],
                    description: "Automatisation des processus"
                }
            ]
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTech(prev => (prev + 1) % techSolutions.length);
        }, 8000);

        return () => clearInterval(interval);
    }, []);

    const floatingTechIcons = [
        <DiReact className="floating-tech-icon" />,
        <DiNodejs className="floating-tech-icon" />,
        <DiAws className="floating-tech-icon" />,
        <DiDocker className="floating-tech-icon" />,
        <SiKubernetes className="floating-tech-icon" />,
        <SiTensorflow className="floating-tech-icon" />
    ];

    return (
        <div className="tech-main-section" ref={sectionRef}>
            {/* Effets de fond futuristes */}
            <div className="tech-background-overlay">
                <div className="matrix-rain"></div>
                <div className="neon-grid"></div>
                <div className="cyber-orbs">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="cyber-orb" style={{ '--orb-delay': `${i * 0.5}s` }}></div>
                    ))}
                </div>
            </div>

            {/* Éléments flottants */}
            <div className="floating-tech-elements">
                {floatingTechIcons.map((Icon, index) => (
                    <div
                        key={index}
                        className="floating-tech-wrapper"
                        style={{ '--float-delay': `${index * 0.8}s` }}
                    >
                        {Icon}
                    </div>
                ))}
            </div>

            {/* Connexions entre sections */}
            <div className="tech-connections">
                <div className="connection-line line-1"></div>
                <div className="connection-line line-2"></div>
                <div className="connection-line line-3"></div>
            </div>

            <div className="tech-main-container">
                {/* Header tech futuriste */}
                <div className="tech-main-header">
                    <div className="tech-title-wrapper">
                        <div className="tech-title-glow">SYSTÈME TECH INTÉGRÉ</div>
                        <h1 className="tech-main-title">
                            <span className="tech-title-word">ARCHITECTURE</span>
                            <span className="tech-title-accent">DIGITALE</span>
                            <span className="tech-title-word">QUANTUM</span>
                        </h1>
                        <div className="tech-title-sub">
                            <span className="tech-sub-text">SOLUTIONS TECH AVANCÉES</span>
                            <div className="tech-sub-icons">
                                <FaCogs />
                                <FaNetworkWired />
                                <FaCloud />
                            </div>
                        </div>
                    </div>

                    {/* Stats en temps réel */}
                    <div className="tech-live-metrics">
                        <div className="metric-item">
                            <div className="metric-value" data-target="99.9">0</div>
                            <div className="metric-label">UPTIME</div>
                            <div className="metric-graph">
                                <div className="graph-bar"></div>
                            </div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value" data-target="24">0</div>
                            <div className="metric-label">/7 SUPPORT</div>
                            <div className="metric-graph">
                                <div className="graph-bar"></div>
                            </div>
                        </div>
                        <div className="metric-item">
                            <div className="metric-value" data-target="1000">0</div>
                            <div className="metric-label">TPS</div>
                            <div className="metric-graph">
                                <div className="graph-bar"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logo central holographique */}
                <div className="tech-logo-center">
                    <div className="hologram-logo-container">
                        <div className="hologram-logo">
                            <img src={logo} alt="Tech Logo" className="tech-logo-img" />
                            <div className="logo-hologram-effect"></div>
                            <div className="logo-glow"></div>
                            <div className="logo-rings">
                                <div className="logo-ring ring-1"></div>
                                <div className="logo-ring ring-2"></div>
                                <div className="logo-ring ring-3"></div>
                            </div>
                        </div>
                        <div className="logo-text">TECH CORE</div>
                    </div>
                </div>

                {/* Grille des solutions tech */}
                <div className="tech-solutions-grid">
                    {techSolutions.map((solution, solutionIndex) => (
                        <div
                            key={solutionIndex}
                            className={`tech-solution-category ${solutionIndex === activeTech ? 'active' : ''}`}
                            onMouseEnter={() => setActiveTech(solutionIndex)}
                        >
                            {/* Header de catégorie */}
                            <div className="category-header">
                                <div className="category-icon">{solutionIndex + 1}</div>
                                <h3 className="category-title">{solution.category}</h3>
                                <div className="category-indicator">
                                    <div className="indicator-light"></div>
                                    <div className="indicator-pulse"></div>
                                </div>
                            </div>

                            {/* Items de la catégorie */}
                            <div className="category-items">
                                {solution.items.map((item, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        className={`tech-solution-item ${hoveredItem === `${solutionIndex}-${itemIndex}` ? 'hovered' : ''}`}
                                        onMouseEnter={() => setHoveredItem(`${solutionIndex}-${itemIndex}`)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                        style={{ '--item-color': item.color }}
                                    >
                                        {/* Effet holographique */}
                                        <div className="item-hologram"></div>

                                        {/* Contenu */}
                                        <div className="item-content">
                                            <div className="item-icon-wrapper">
                                                <div className="item-icon" style={{ color: item.color }}>
                                                    {item.icon}
                                                </div>
                                                <div className="item-icon-glow"></div>
                                            </div>

                                            <h4 className="item-title">{item.name}</h4>

                                            <p className="item-description">{item.description}</p>

                                            {/* Stack technologique */}
                                            <div className="item-tech-stack">
                                                {item.tech.map((tech, techIndex) => (
                                                    <span
                                                        key={techIndex}
                                                        className="tech-tag"
                                                        style={{ '--tag-delay': `${techIndex * 0.1}s` }}
                                                    >
                            {tech}
                          </span>
                                                ))}
                                            </div>

                                            {/* Stats */}
                                            <div className="item-stats">
                                                <div className="stat">
                                                    <div className="stat-value">99%</div>
                                                    <div className="stat-label">Perf</div>
                                                </div>
                                                <div className="stat">
                                                    <div className="stat-value">∞</div>
                                                    <div className="stat-label">Scale</div>
                                                </div>
                                                <div className="stat">
                                                    <div className="stat-value">AI</div>
                                                    <div className="stat-label">Boost</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Effets de bordure */}
                                        <div className="item-border"></div>

                                        {/* Points de connexion */}
                                        <div className="item-connection-points">
                                            <div className="connection-point"></div>
                                            <div className="connection-point"></div>
                                            <div className="connection-point"></div>
                                            <div className="connection-point"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Ligne de connexion */}
                            {solutionIndex < techSolutions.length - 1 && (
                                <div className="category-connector">
                                    <div className="connector-line"></div>
                                    <div className="connector-dots">
                                        <div className="connector-dot"></div>
                                        <div className="connector-dot"></div>
                                        <div className="connector-dot"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Barre de status */}
                <div className="tech-status-bar">
                    <div className="status-indicators">
                        <div className="status-indicator active">
                            <div className="indicator-label">SYSTEM</div>
                            <div className="indicator-light"></div>
                        </div>
                        <div className="status-indicator">
                            <div className="indicator-label">NETWORK</div>
                            <div className="indicator-light"></div>
                        </div>
                        <div className="status-indicator">
                            <div className="indicator-label">SECURITY</div>
                            <div className="indicator-light"></div>
                        </div>
                        <div className="status-indicator">
                            <div className="indicator-label">CLOUD</div>
                            <div className="indicator-light"></div>
                        </div>
                    </div>

                    <div className="status-info">
                        <div className="status-text">
                            <span className="status-active">ACTIVE:</span>
                            <span className="status-value">{techSolutions[activeTech].category}</span>
                        </div>
                        <div className="status-progress">
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${(activeTech + 1) * 25}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Code défilant */}
                <div className="tech-code-display">
                    <div className="code-scroller">
            <pre className="code-content">
{`// SYSTEM ARCHITECTURE - APPLICODETECH
const quantumSystem = {
  architecture: "Microservices",
  infrastructure: "Cloud-Native",
  security: "Zero-Trust",
  monitoring: "Real-Time AI",
  scalability: "Auto-Scaling",
  deployment: "CI/CD Automated"
};

class DigitalTransformation {
  constructor() {
    this.technologies = [
      "AI/ML Integration",
      "Blockchain Solutions",
      "IoT Connectivity",
      "Edge Computing",
      "Quantum Ready"
    ];
    this.performance = "99.99% Uptime";
    this.support = "24/7 AI Monitoring";
  }
}`}
            </pre>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TechMainSection;